import { gatewayInstance } from "../server/gateway";
import { CreateTaskForm, TaskModel, WorkUserInfo } from "@/types/work";
import {
  NewTaskModel,
  TaskListParams,
  TaskListByTaskGroupParams,
  IEditTaskItemParam,
  RecordsParams,
  TaskGroupModel
} from "@/types/newWork";
import { Method, pwaAttribute } from "@/attributes/pwaAttribute";
import BaseController from "@/attributes/BaseController";

import { changeGovUrl } from "@/utils/tools";

class WorkController extends BaseController {
  // 序列化
  serialFn(obj: any) {
    if (typeof obj === "object") {
      let arr = Object.keys(obj).sort();
      return arr.reduce((str0, v) => {
        let str = str0;
        if (obj[v] || obj[v] === 0) {
          str += `&${v}=${obj[v].toString()}`;
        }
        return str;
      }, "");
    }
    return "";
  }
  // 判断是否存在本地数据库，没有则return
  getOfflineTask(propertyKey: string, ...args: any) {
    let key = "";
    switch (propertyKey) {
      case "task._getTaskDetail_":
        key = `${propertyKey}?taskId=${args[0]}`;
        break;
      case "task._queryTasks_":
      case "task._queryTasksByTaskGroup_":
      case "task._queryNoGroupTasks_":
      case "task._findTaskComments_":
      case "task._queryTaskUpdateRecords_":
      case "task._queryPersonalWholeTaskCount_":
      case "task._queryTaskGroupStatistic_":
      case "task._queryTaskGroupDailyStatistic_":
      case "task._queryTaskGroupTaskFinishedList_":
      case "task._queryDataManagementList_":
        key = propertyKey + "?" + this.serialFn(args[0]);
        break;
      default:
        key = propertyKey;
    }
    return new Promise((resolve, reject) => {
      this.dbService.dbService
        .getFromDB(key)
        .then((doc: any) => {
          if (!doc) {
            throw new Error("网络异常(离线)");
          }
          // 离线情况下获取到任务详情的缓存，需要把‘未查看’状态给换为‘进行中’
          if (propertyKey === "task._getTaskDetail_" && doc?.data?.processStatus === 4) {
            resolve({ ...doc?.data, processStatus: 1 });
          }
          resolve(doc?.data);
        })
        .catch((e: any) => {
          console.error(e);
          reject(e);
        });
    });
  }
  // 判断是否存在本地数据库，没有则创建
  setOfflineTask(propertyKey: string, data: any, ...args: any) {
    let key = "";
    switch (propertyKey) {
      case "task._getTaskDetail_":
        key = `${propertyKey}?taskId=${args[0]}`;
        break;
      case "task._queryTaskUpdateRecords_":
      case "task._findTaskComments_":
      case "task._queryTasks_":
      case "task._queryTasksByTaskGroup_":
      case "task._queryNoGroupTasks_":
      case "task._queryPersonalWholeTaskCount_":
      case "task._queryTaskGroupStatistic_":
      case "task._queryTaskGroupDailyStatistic_":
      case "task._queryTaskGroupTaskFinishedList_":
      case "task._queryDataManagementList_":
        key = propertyKey + "?" + this.serialFn(args[0]);
        break;
      default:
        key = propertyKey;
    }

    return new Promise((resolve, reject) => {
      this.dbService.dbService
        .saveToDB({
          _id: key,
          data,
          params: args
        })
        .then(() => {
          resolve("保存成功");
        })
        .catch((e: any) => {
          console.error(e);
          reject(e);
        });
    });
  }
}

const workDber = new WorkController("task.db", "taskGeneralSheet");
class WorkOfflineApi {
  static restored(fn: (...opts: any) => Promise<any>) {
    return function (...args: any[]) {
      return fn(...args)
        .then((data: any) => {
          return [data as any, null];
        })
        .catch((error: any) => {
          let offlineMsg = "";
          let isOffline;
          if (
            error instanceof Error &&
            error.message.includes("网络") &&
            error.message.includes("离线")
          ) {
            // offlineMsg = '您已离线，故无法查看';
            offlineMsg = "网络状态异常，请稍候再试";
            isOffline = true;
          }
          return [
            null,
            offlineMsg || error.message?.toString() || error.errorMsg || error || "服务器错误",
            isOffline
          ];
        });
    };
  }

  @pwaAttribute(Method.get, workDber, "getOfflineTask", "setOfflineTask", "task._getTaskDetail_")
  getTaskDetail_(taskId: string, share = "") {
    return gatewayInstance.request({
      api: "work.task.queryTaskInfo",
      method: "POST",
      body: { taskId, share }
    });
  }

  @pwaAttribute(Method.get, workDber, "getOfflineTask", "setOfflineTask", "task._queryTasks_")
  queryTasks_(opts: {
    fieldSortType: number; // 1: 创建时间降序，2: 截止日期升序，3: 创建时间升序，4: 截止日期降序，5: 开始时间升序，6: 开始日期降序
    userRole: number | null; // 0:创建人, 1:负责人 2:协同人 null:全部
    taskStatuses: number[]; // 任务状态:1-未完成 2-已完成 3-已中止
    processStatuses: number[]; // 1:推进中 2:已完成 3:有困难 4:未查看
    priorityTypes: number[]; // 任务类型 1:B2 2:B1 3:A2 4:A1"
    limit: number;
    offset?: number;
  }) {
    return gatewayInstance.request({
      api: "work.task.queryTasks",
      method: "POST",
      body: opts
    });
  }

  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryTasksByTaskGroup_"
  )
  queryTasksByTaskGroup_(opts: {
    fieldSortType: number; // 1: 创建时间排序，2: 截止时间排序
    groupId: string; // 0:创建人, 1:负责人 2:协同人
    priorityTypes?: number[]; // 任务类型 1:B2 2:B1 3:A2 4:A1"
    limit: number;
    offset?: number;
  }) {
    return gatewayInstance.request({
      api: "work.task.queryTasksByTaskGroup",
      method: "POST",
      body: opts
    });
  }

  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryNoGroupTasks_"
  )
  queryNoGroupTasks_(opts: {
    fieldSortType: number; // 1: 创建时间排序，2: 截止时间排序
    priorityTypes?: number[]; // 任务类型 1:B2 2:B1 3:A2 4:A1"
    limit: number;
    offset?: number;
  }) {
    return gatewayInstance.request({
      api: "work.task.queryNoGroupTasks",
      method: "POST",
      body: opts
    });
  }

  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryTaskUpdateRecords_"
  )
  getTaskUpdateRecords_(opt: RecordsParams) {
    return gatewayInstance.request({
      api: "work.task.queryTaskUpdateRecords",
      method: "POST",
      body: opt
    });
  }

  queryTaskProcessAuditList_(opt: { taskId: string }) {
    return gatewayInstance.request({
      api: "work.task.queryTaskProcessAuditList",
      method: "POST",
      body: opt
    });
  }

  auditTaskProcess_(opt: {
    auditId: string;
    taskId: string;
    auditStatus?: number;
    auditComments?: string;
  }) {
    return gatewayInstance.request({
      api: "work.task.auditTaskProcess",
      method: "POST",
      body: opt
    });
  }

  @pwaAttribute(Method.get, workDber, "getOfflineTask", "setOfflineTask", "task._findTaskComments_")
  findTaskComments(opt: { taskId: string; offset?: number; limit?: number }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.findTaskComments",
      method: "POST",
      body: opt
    });
  }

  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryPersonalWholeTaskCount_"
  )
  queryPersonalWholeTaskCount(opt: { teamId: string; timeRangeType: number }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.queryPersonalWholeTaskCount",
      method: "POST",
      body: opt
    });
  }

  // 任务分组任务状态统计总数
  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryTaskGroupStatistic_"
  )
  queryTaskGroupStatistic(opt: {
    groupId: string;
    beginDate?: string;
    endDate?: string;
  }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.queryTaskGroupStatistic",
      method: "POST",
      body: opt
    });
  }

  // 任务分组状态统计折线图
  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryTaskGroupDailyStatistic_"
  )
  queryTaskGroupDailyStatistic(opt: {
    groupId: string;
    beginDate?: string;
    endDate?: string;
  }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.queryTaskGroupDailyStatistic",
      method: "POST",
      body: opt
    });
  }

  // 任务分组完成排行
  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryTaskGroupTaskFinishedList_"
  )
  queryTaskGroupTaskFinishedList(opt: {
    groupId: string;
    type: number; // 1：法人单位，2：部门，3：人员，4：兼岗部门
    beginDate?: string;
    endDate?: string;
    limit?: number; // 限制大小，不需要可不传
  }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.queryTaskGroupTaskFinishedList",
      method: "POST",
      body: opt
    });
  }

  // 任务分组查询兼岗部门
  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._queryDataManagementList_"
  )
  queryDataManagementList(opt: { groupId: string }): Promise<any> {
    return gatewayInstance.request({
      api: "work.task.queryDataManagementList",
      method: "POST",
      body: opt
    });
  }

  @pwaAttribute(
    Method.get,
    workDber,
    "getOfflineTask",
    "setOfflineTask",
    "task._getTaskListFieldConfig_"
  )
  getTaskListFieldConfig() {
    return gatewayInstance.request({
      api: "work.task.getTaskListFieldConfig",
      method: "POST"
    });
  }
}

const theWorkOffline = new WorkOfflineApi();
export const getTaskDetail = WorkOfflineApi.restored(theWorkOffline.getTaskDetail_);
export const queryTasks = WorkOfflineApi.restored(theWorkOffline.queryTasks_);
export const queryTasksByTaskGroup = WorkOfflineApi.restored(theWorkOffline.queryTasksByTaskGroup_);
export const queryNoGroupTasks = WorkOfflineApi.restored(theWorkOffline.queryNoGroupTasks_);
export const getTaskUpdateRecords = WorkOfflineApi.restored(theWorkOffline.getTaskUpdateRecords_);
export const queryTaskProcessAuditList = WorkOfflineApi.restored(
  theWorkOffline.queryTaskProcessAuditList_
);
export const auditComments = WorkOfflineApi.restored(theWorkOffline.auditTaskProcess_);
export const findTaskComments = WorkOfflineApi.restored(theWorkOffline.findTaskComments);
export const queryPersonalWholeTaskCount = WorkOfflineApi.restored(
  theWorkOffline.queryPersonalWholeTaskCount
);
export const queryTaskGroupStatistic = WorkOfflineApi.restored(
  theWorkOffline.queryTaskGroupStatistic
);
export const queryTaskGroupDailyStatistic = WorkOfflineApi.restored(
  theWorkOffline.queryTaskGroupDailyStatistic
);
export const queryTaskGroupTaskFinishedList = WorkOfflineApi.restored(
  theWorkOffline.queryTaskGroupTaskFinishedList
);
export const queryDataManagementList = WorkOfflineApi.restored(
  theWorkOffline.queryDataManagementList
);
export const getTaskListFieldConfig = WorkOfflineApi.restored(
  theWorkOffline.getTaskListFieldConfig
);

export function getErrMessage(str: string) {
  return str.substr(str.indexOf("]") + 1);
}

/**
 * @description 新的创建任务接口
 */
export async function createTask(task: Partial<NewTaskModel>) {
  try {
    if (task.resources && task.resources.length) {
      console.log("task.resources----before", task.resources, JSON.stringify(task.resources));
      let resources = task.resources as any;
      task.resources = [] as any;
      resources.forEach((newItem: any) => {
        if (newItem.fileUrl) {
          newItem.fileUrl = changeGovUrl(newItem.fileUrl);
        }
        if (newItem.url) {
          newItem.url = changeGovUrl(newItem.url);
        }
        task.resources?.push(newItem);
      });
    }
    console.log("task.resources----after", task.resources, JSON.stringify(task.resources));
    const data = await gatewayInstance.request({
      api: "work.task.createTask",
      method: "POST",
      body: task
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * @description 新的修改任务接口
 */
export async function modifyTaskInfo(task: Partial<NewTaskModel>) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.modifyTask",
      method: "POST",
      body: task
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * @description 新的修改任务接口
 */
export async function modifyWholeTask(opts: Partial<NewTaskModel & TaskGroupModel>) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.modifyWholeTask",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * @description 新的查看任务详情接口
 */
export async function getTaskDetail0(taskId: string, share = "") {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryTaskInfo",
      method: "POST",
      body: { taskId, share }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message?.toString() || error.errorMsg || error || "服务器错误"];
  }
}

/**
 * @description 新的查询任务推进记录
 * @param taskId
 * @param offset // 当前页
 * @param limit //条数
 * @param userId // 查看个人推进记录
 * @param teamId // 查看个人推进记录
 * @param appId // 查看个人推进记录
 */
export async function getTaskUpdateRecords0(opt: RecordsParams) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryTaskUpdateRecords",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function createPersonalTaskApi(
  opts: Partial<TaskModel> & { endTimeFrom?: string; callback?: any }
): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.userTaskSave",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function createPersonalTaskInfoApi(
  opts: Partial<CreateTaskForm> & { endTimeFrom?: string; callback?: any }
): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.clientTaskSave",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, getErrMessage(error.message.toString())];
  }
}

export async function updatePersonalTask(task: Partial<TaskModel>) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.taskUpdate",
      method: "POST",
      body: task
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function updateTaskProcess(opt: {
  taskId: string;
  taskStatus?: number;
  processStatus?: number;
  front?: string;
  after?: string;
  endTimeFrom?: string;
  endTimeTo?: string;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.updateTaskProcess",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function getPersonalList(opts: {
  endTimeFrom?: string;
  endTimeTo?: string;
  offset: number;
  limit: number;
  projectId: string;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.findMyTasks",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function getPersonalTaskDetail(taskId: string, share = "") {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.getTaskDetail",
      method: "POST",
      body: { taskId, share }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function addTaskMembers(opt: {
  taskId: string;
  endTimeFrom?: string;
  endTimeTo?: string;
  members: {
    userId: string;
    teamId: string;
    app: string;
    taskUserRole: number;
  }[];
}) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.saveTaskMembers",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function updateTaskMembers(opt: {
  taskId: string;
  endTimeFrom?: string;
  endTimeTo?: string;
  members: {
    userId: string;
    teamId: string;
    app: string;
    taskUserRole: number;
  }[];
}) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.updateTaskMembers",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function addTaskResources(opt: {
  taskId?: string;
  resource?: {
    name: string;
    url: string;
    bizType: number;
    bizId: string;
    size: number;
    resourceType: number;
    fileType: string;
    creatorUserId: string;
  };
  resources?: {
    name: string;
    url: string;
    bizType: number;
    bizId: string;
    size: number;
    resourceType: number;
    fileType: string;
    creatorUserId: string;
  }[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.saveTaskResources",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function taskSortUpdate(opt: {
  after?: string;
  front?: string;
  moveTaskId: string;
  endTime?: string | null;
  endTimeTo?: string;
  endTimeFrom?: string;
  timeclassify?: number;
  projectId: string;
}) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.tasksSortUpdate",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 点赞或取消
 * @param taskId
 * @param like
 */
export async function taskPraise(taskId: string, like: boolean): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: like ? "work.task.taskPraise" : "work.task.cancelTaskPraise",
      method: "POST",
      body: { taskId }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function findTaskPraise(opt: {
  taskId: string;
  limit: number;
  offset: number;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.findTaskPraises",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function delTaskResources(opt: {
  taskId?: string;
  resourceIds: string[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.delTaskResources",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function delTaskMembers(opt: {
  taskId?: string;
  members: {
    userId: string;
    teamId: string;
    app: string;
    taskUserRole: number;
  }[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.delTaskMembers",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function saveTaskComment(opt: {
  taskId: string;
  parentId?: string;
  content: string;
  userIds?: string[];
  type: number;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.saveTaskComment",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function findTaskComments0(opt: {
  taskId: string;
  offset?: number;
  limit?: number;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.findTaskComments",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export async function deleteTaskComment(opt: { commentId: string }): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.deleteTaskComment",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 *
 * type: 删除类型，1:不递归删除子任务 2:递归删除子任务(会删除)
 * quit: true 退出任务 false 删除任务
 */
export async function deleteTask(opt: {
  taskId: string;
  type: 1 | 2; // 默认1
  quit?: boolean; // 默认false
  delGroup?: boolean; // 是否解散关联的组
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.deleteTask",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, getErrMessage(error.message.toString())];
  }
}

/**
 * 任务创建群聊
 */
export async function createGroup(opt: {
  taskId: string;
  groupName: string;
  users: { userId: string; teamId: string; app: string }[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.project.createGroup",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 任务变更归属
 */
export async function updateTaskSource(opt: {
  endTimeFrom?: string;
  endTimeTo?: string;
  taskId: string;
  sourceId: string;
  source: "USER" | "PROJECT" | "PROCESS" | "LOW_CODE";
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.updateTaskSource",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 任务导出查询列表
export async function findForExportMyTasks(opt: {
  title?: String;
  startTime?: string;
  endTime?: string;
  priorityTypes: number[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.findForExportMyTasks",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 任务导出接口
export async function exportMyTasks(opt: {
  title?: String;
  startTime?: string;
  endTime?: string;
  priorityTypes: number[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.exportMyTasks",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// -----------start ---------------
export async function getTaskList(opts: TaskListParams): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryTaskList",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function queryTasks0(opts: {
  fieldSortType: number; // 1: 创建时间排序，2: 截止时间排序
  userRole: number | null; // 0:创建人, 1:负责人 2:协同人 null:全部
  taskStatuses: number[]; // 任务状态:1-未完成 2-已完成 3-已中止
  processStatuses: number[]; // 1:推进中 2:已完成 3:有困难 4:未查看
  priorityTypes: number[]; // 任务类型 1:B2 2:B1 3:A2 4:A1"
  limit: number;
  offset?: number;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryTasks",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}

/**
 * 根据taskId 查询协作人
 */
export async function queryExecutorsByTaskId(taskId: string): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryExecutorsByTaskId",
      method: "POST",
      body: { taskId }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}

export async function editTaskItem(opts: IEditTaskItemParam): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.modifyTask",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

interface MakeTaskGroup {
  groupName: string;
  groupId?: string;
  scope: number;
  userList: Array<{ userId: string; teamId: string; app: string; permission: number }>;
}

export interface CreateTaskGroupType {
  taskId: string;
  from: {
    app: string;
    pin: string;
    clientType?: string;
    teamId: string;
    userId: string;
  };
  body: {
    modifyFiled: number;
    nickName: string;
    name: string;
    notice: string;
    gid?: string;
    invitees: {
      pin: string;
      app: string;
      nickName: string;
      teamId: string;
      userId: string;
    }[];
  };
  kind: number;
  subKind: number;
}

export async function createTaskGroup(params: CreateTaskGroupType): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.createGroup",
      method: "POST",
      body: params
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, getErrMessage(error.message.toString())];
  }
}

export async function checkWhenDeleteTaskExcutors(params: {
  taskId: "string";
  executors: WorkUserInfo[];
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.checkWhenDeleteTaskExcutors",
      method: "POST",
      body: params
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, getErrMessage(error.message.toString())];
  }
}
/* 以下是‘任务组’ */
export async function queryTaskGroups(): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.queryTaskGroups",
      method: "POST"
    });
    // eslint-disable-next-line no-debugger
    // debugger;
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}
export async function makeATaskGroup(params: MakeTaskGroup): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.createTaskGroup",
      method: "POST",
      body: params
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}
export async function editTaskGroup(params: MakeTaskGroup): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.modifyTaskGroup",
      method: "POST",
      body: params
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}
export async function getTaskGroupDetail(groupId: string): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.getTaskGroupDetail",
      method: "POST",
      body: { groupId }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}
export async function removeTaskGroup(
  groupId: string,
  isRemoveTask?: boolean
): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.removeTaskGroup",
      method: "POST",
      body: { groupId, isRemoveTask: !!isRemoveTask }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}
// 任务组归档 or 取消归档
export async function archiveTaskGroup(
  groupId: string,
  archiveType: number
): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.archiveTaskGroup",
      method: "POST",
      body: { groupId, archiveType }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message];
  }
}

// 任务置顶
export interface TaskTopType {
  jsfOperator: {
    userId: string;
    teamId: string;
    app: string;
    clientType?: string;
  };
  fromMQ?: boolean;
  taskId: string;
  operateType: number;
}

export async function setTaskTop(opt: TaskTopType): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.setTaskTop",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * @description 取消关注
 */
export async function cancelTaskSubscribe(
  opt: Omit<TaskTopType, "operateType">
): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.cancelTaskSubscribe",
      method: "POST",
      body: opt
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * @description 任务分组更新或插入兼岗部门设置
 */
export interface userDeptListType {
  userId: string;
  teamId: string;
  deptId: string;
}
export interface TaskManagementType {
  groupId: string;
  userDeptList: userDeptListType[];
}

export async function batchSaveDataManagement(opts: TaskManagementType) {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.batchSaveDataManagement",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export interface batchImportType {
  jsfOperator: {
    userId: string;
    teamId: string;
    app: string;
    clientType?: string;
  };
  fromMQ?: boolean;
  fileUrl: string;
  groupId: string;
}
//  批量导入任务
export async function batchImport(params: batchImportType): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.batchImport",
      method: "POST",
      body: params
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, getErrMessage(error.message.toString())];
  }
}

//  获取任务wps  code
export async function getworkWpsPreViewCode(opts: {
  fileUrl: string;
  taskId?: string;
  userId?: string;
  teamId?: string;
  fileId: string;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.getWpsPreViewCode",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, getErrMessage(error.message.toString())];
  }
}

// 保存任务列表表头配置
export async function saveTaskListFieldConfig(opts: {
  fieldJson: string;
}): Promise<[any, string | null]> {
  try {
    const data = await gatewayInstance.request({
      api: "work.task.saveTaskListFieldConfig",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, getErrMessage(error.message.toString())];
  }
}
